import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Group,
  Paper,
  Text,
  Stack,
  Title,
  Alert,
} from "@mantine/core";
import {
  IconCircleCheck,
  IconX,
  IconRotate,
} from "@tabler/icons";
import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";

const formatTime = (seconds) => {
  if (!seconds) return "--";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const QuizResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [api] = useServerApi();
  const [data, setData] = useState(location.state?.review ?? null);
  const [reviewMode, setReviewMode] = useState(false);

  useEffect(() => {
    if (data) return;
    const load = async () => {
      const res = await api.Quiz.getResult(id);
      if (res?.data) {
        setData({
          result: res.data.result,
          passed: res.data.result?.status === "PASSED",
          questions: res.data.questions,
        });
      }
    };
    load();
  }, [id, data]);

  if (!data?.result) {
    return (
      <Page title="Quiz Result">
        <Text color="dimmed">Loading result...</Text>
      </Page>
    );
  }

  const { result, passed, questions = [] } = data;
  const quiz = result.quiz ?? {};
  const canRetake = result.canRetake && !passed;

  const handleRetake = async () => {
    await api.Quiz.retake(id);
    navigate(`/consultant/myQuizzes/${id}`);
  };

  return (
    <Page title="Quiz Result">
      <Stack align="center" spacing="lg" mt="xl">
        {passed ? (
          <IconCircleCheck size={80} color="#40c057" stroke={1.5} />
        ) : (
          <IconX size={80} color="#fa5252" stroke={1.5} />
        )}

        <Title order={2} color={passed ? "green" : "red"}>
          {passed ? "Congratulations!" : "Quiz Not Passed"}
        </Title>
        <Text color="dimmed">
          {passed
            ? "You have passed the quiz"
            : "You did not meet the passing threshold"}
        </Text>

        <Paper
          p="xl"
          withBorder
          radius="md"
          sx={(theme) => ({
            borderColor: passed ? theme.colors.green[5] : theme.colors.red[5],
            minWidth: 320,
          })}
        >
          <Stack align="center" spacing="xs">
            <Text size="sm" color="dimmed">
              Your Score
            </Text>
            <Text size={48} weight={700} color={passed ? "green" : "red"}>
              {result.score}%
            </Text>
            <Text>
              {result.correctCount}/
              {questions.length || result.answers?.length || "?"} Correct
            </Text>
            <Text size="sm">Time: {formatTime(result.timeSpent)}</Text>
            <Text size="sm" color="dimmed">
              {passed
                ? `Above passing threshold of ${quiz.passingRate ?? 70}%`
                : `Passing threshold: ${quiz.passingRate ?? 70}% required`}
            </Text>
          </Stack>
        </Paper>

        {!passed && canRetake && (
          <Alert icon={<IconRotate size={16} />} color="yellow" variant="light">
            Attempt {result.attemptNumber} of {quiz.maxRetakeCount ?? 100} —
            You can retake this quiz
          </Alert>
        )}

        {reviewMode && (
          <Stack spacing="md" sx={{ width: "100%" }}>
            {questions.map((q, qi) => {
              const answer = result.answers?.find(
                (a) => String(a.question) === String(q._id)
              );
              return (
                <Paper key={q._id} p="md" withBorder>
                  <Text weight={600} mb="xs">
                    Q{qi + 1}. {q.questionText}
                  </Text>
                  {(q.options ?? []).map((opt, oi) => (
                    <Text
                      key={oi}
                      size="sm"
                      color={
                        opt.isCorrect
                          ? "green"
                          : answer?.selectedOption === oi
                          ? "red"
                          : undefined
                      }
                    >
                      {opt.text}
                      {opt.isCorrect && " ✓"}
                      {!opt.isCorrect &&
                        answer?.selectedOption === oi &&
                        " ✗"}
                    </Text>
                  ))}
                </Paper>
              );
            })}
          </Stack>
        )}

        <Group>
          {(passed || result.status === "FAILED") && !reviewMode && (
            <Button variant="outline" onClick={() => setReviewMode(true)}>
              Review Answers
            </Button>
          )}
          {!passed && canRetake && (
            <Button color="red" onClick={handleRetake}>
              Retake Quiz
            </Button>
          )}
          <Button
            variant="default"
            onClick={() => navigate("/consultant/myQuizzes")}
          >
            Back to My Quizzes
          </Button>
        </Group>
      </Stack>
    </Page>
  );
};

export default QuizResult;
